import type { Moment } from 'moment';
import moment from 'moment';

import { resolveUserDisplayName } from '@src/packages/utils/user';

import type { TCoverReplaceDurationMode } from '../types';

/** Sentinel end date for permanent shift replacements (exception requires YYYY-MM-DD). */
export const PERMANENT_REPLACEMENT_END_DATE = '2099-12-31';

export type ResolveCoverReplaceExceptionDatesParams = {
  durationMode: TCoverReplaceDurationMode;
  startDate: Moment | null;
  endDate: Moment | null;
  /** Fallback when permanent mode clears form dates (cover day understaffed). */
  coverDate?: string;
};

export type CoverReplaceExceptionDates = {
  startDate: string;
  endDate: string;
};

/**
 * Resolves exception start/end dates (YYYY-MM-DD) from the cover-replace step-2 form.
 */
export const resolveCoverReplaceExceptionDates = (
  params: ResolveCoverReplaceExceptionDatesParams,
): CoverReplaceExceptionDates | null => {
  const { durationMode, startDate, endDate, coverDate } = params;

  if (durationMode === 'permanent') {
    const start =
      coverDate && moment(coverDate, 'YYYY-MM-DD', true).isValid()
        ? coverDate
        : moment().format('YYYY-MM-DD');
    return { startDate: start, endDate: PERMANENT_REPLACEMENT_END_DATE };
  }

  if (!startDate || !startDate.isValid()) {
    return null;
  }

  const start = startDate.format('YYYY-MM-DD');

  if (durationMode === 'single') {
    return { startDate: start, endDate: start };
  }

  if (!endDate || !endDate.isValid() || endDate.isBefore(startDate, 'day')) {
    return null;
  }

  return { startDate: start, endDate: endDate.format('YYYY-MM-DD') };
};

export type ExceptionUserSummaryInput = {
  id: string;
  displayName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  idCard?: string;
  photoURL?: string | null;
  locale?: string;
  phone?: string;
  flexTime?: boolean;
  professionalEmail?: string;
  birthDate?: unknown;
};

/**
 * Builds a compact user summary suitable for exception.user / replacedUser.
 * Prefer a full org user document when available.
 */
export const toExceptionUserSummary = (
  user: ExceptionUserSummaryInput | null | undefined,
): Record<string, unknown> | null => {
  if (!user?.id) return null;

  const displayName =
    resolveUserDisplayName({
      firstName: user.firstName,
      lastName: user.lastName,
      displayName: user.displayName,
    }) ||
    user.email ||
    user.id;

  const summary: Record<string, unknown> = {
    id: user.id,
    displayName,
    email: user.email ?? '',
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    idCard: user.idCard ?? '',
  };

  if (user.photoURL != null) summary.photoURL = user.photoURL;
  if (user.locale != null) summary.locale = user.locale;
  if (user.phone != null) summary.phone = user.phone;
  if (user.flexTime != null) summary.flexTime = user.flexTime;
  if (user.professionalEmail != null) summary.professionalEmail = user.professionalEmail;
  if (user.birthDate != null) summary.birthDate = user.birthDate;

  return summary;
};
